import { useEffect, useState, useMemo } from "react";
import {
  Plus, FileText, Download, Edit2, Trash2, Send, Check, X, DollarSign,
  Loader2, ChevronDown, ChevronUp, Receipt, AlertTriangle,
} from "lucide-react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS = [
  { value: "borrador", label: "Borrador", color: "#94a3b8" },
  { value: "presentada", label: "Presentada", color: "#22d3ee" },
  { value: "autorizada", label: "Autorizada", color: "#16a34a" },
  { value: "facturada", label: "Facturada", color: "#f97316" },
  { value: "cobrada", label: "Cobrada", color: "#22c55e" },
  { value: "pagada", label: "Pagada", color: "#3F2A91" },
  { value: "rechazada", label: "Rechazada", color: "#dc2626" },
];
function statusBadge(s) {
  const m = STATUS.find((x) => x.value === s) || STATUS[0];
  return (
    <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest"
      style={{ background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}66` }}>
      {m.label}
    </span>
  );
}

const EMPTY_ITEM = { description: "", unit: "", unit_price: 0, qty_period: 0, qty_cumulative: 0, budget_item_id: null };
const EMPTY = {
  folio: "",
  period_start: todayLocal(),
  period_end: todayLocal(),
  iva_pct: 16,
  items: [],
  amortizations: [],
  retentions: [],
  physical_progress_pct: 0,
  notes: "",
};

export default function ContractEstimaciones({ contract, budgetItems = [], onRefresh }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/estimaciones`);
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);

  // ===== Acciones de workflow =====
  const submit = async (e) => {
    if (!confirm(`¿Presentar estimación #${e.number} para autorización?`)) return;
    try {
      await api.post(`/estimaciones/${e.id}/submit`);
      toast.success("Estimación presentada");
      load();
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const approve = async (e) => {
    if (!confirm(`¿Autorizar estimación #${e.number}?`)) return;
    try {
      await api.post(`/estimaciones/${e.id}/approve`);
      toast.success("Estimación autorizada");
      load();
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const pay = async (e) => {
    if (!confirm(`¿Marcar como PAGADA la estimación #${e.number}?\nNeto a pagar: ${fmtMXN(e.net_to_pay)}`)) return;
    try {
      await api.post(`/estimaciones/${e.id}/pay`);
      toast.success("Estimación pagada");
      load();
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const reject = async (e) => {
    const reason = prompt("Motivo del rechazo:");
    if (!reason?.trim()) return;
    try {
      await api.post(`/estimaciones/${e.id}/reject`, { rejected_reason: reason });
      toast.success("Estimación rechazada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (e) => {
    if (!confirm(`¿Eliminar estimación #${e.number}?`)) return;
    try {
      await api.delete(`/estimaciones/${e.id}`);
      toast.success("Eliminada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const downloadPdf = async (e) => {
    try {
      const res = await api.get(`/estimaciones/${e.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimacion_${String(e.number).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const downloadControlPdf = async (e) => {
    try {
      const res = await api.get(`/estimaciones/${e.id}/control/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `control_estimacion_${String(e.number).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el Control de Estimaciones"); }
  };
  const invoiceEstimacion = async (e) => {
    if (!confirm(`¿Facturar la estimación #${e.number}?\nSaldo a pagar según control (con amortización y retenciones). Se creará con folio automático y formato IGIFE.`)) return;
    try {
      const { data } = await api.post(`/estimaciones/${e.id}/client-invoice`);
      toast.success(`Factura ${data.folio} creada por ${fmtMXN(data.total)}`);
      window.dispatchEvent(new Event("ci-refresh"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error al facturar"); }
  };

  // ===== Render =====
  const totalsBar = useMemo(() => {
    const aprobadas = items.filter((x) => ["autorizada", "pagada"].includes(x.status));
    const pagadas = items.filter((x) => x.status === "pagada");
    return {
      count: items.length,
      total_aprobado: aprobadas.reduce((s, x) => s + (x.total_period || 0), 0),
      neto_pagado: pagadas.reduce((s, x) => s + (x.net_to_pay || 0), 0),
      pct_facturado: items.length ? (items[items.length - 1].financial_cumulative_pct || 0) : 0,
    };
  }, [items]);

  return (
    <div data-testid="contract-estimaciones-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-mono">// ESTIMACIONES DEL CONTRATO</div>
          <div className="text-xs text-slate-400">
            {totalsBar.count} estimación{totalsBar.count !== 1 ? "es" : ""} ·
            {" "}{fmtMXN(totalsBar.total_aprobado)} aprobado ·
            {" "}{totalsBar.pct_facturado.toFixed(1)}% facturado vs contrato
          </div>
        </div>
        <button
          data-testid="new-estimacion-btn"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva estimación
        </button>
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="panel p-8 text-center text-slate-400">
          <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          Aún no hay estimaciones. Crea la primera para empezar a cobrar.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => {
            const expanded = expandedId === e.id;
            const canEdit = ["borrador", "rechazada"].includes(e.status) || ["admin", "contador"].includes(user?.role);
            const canSubmit = ["borrador", "rechazada"].includes(e.status);
            const canApprove = e.status === "presentada" && isAdmin;
            const canPay = e.status === "autorizada" && isAdmin;

            return (
              <div key={e.id} data-testid={`estimacion-row-${e.id}`} className="panel">
                <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <button onClick={() => setExpandedId(expanded ? null : e.id)} className="btn-ghost p-1">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <span className="font-display font-bold text-sm text-slate-100 min-w-[3rem]">#{String(e.number).padStart(2, "0")}</span>
                  {statusBadge(e.status)}
                  <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
                    {fmtDate(e.period_start)} → {fmtDate(e.period_end)}
                    {e.folio && <span className="ml-2 text-slate-500 font-mono">· {e.folio}</span>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-bold text-cyan-300 tabular-nums">{fmtMXN(e.net_to_pay)}</div>
                    <div className="text-[10px] text-slate-500">neto a pagar</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`est-pdf-${e.id}`} onClick={() => downloadPdf(e)}
                      title="PDF" className="btn-ghost p-1.5"><Download className="w-3.5 h-3.5" /></button>
                    <button data-testid={`est-control-pdf-${e.id}`} onClick={() => downloadControlPdf(e)}
                      title="Control de Estimaciones (formato dependencia)" className="btn-ghost p-1.5" style={{ color: "var(--cyan)" }}>
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                    {canEdit && (
                      <button data-testid={`est-edit-${e.id}`} onClick={() => { setEditing(e); setOpen(true); }}
                        title="Editar" className="btn-ghost p-1.5"><Edit2 className="w-3.5 h-3.5" /></button>
                    )}
                    {canSubmit && (
                      <button data-testid={`est-submit-${e.id}`} onClick={() => submit(e)}
                        title="Presentar" className="btn-ghost p-1.5" style={{ color: "var(--cyan)" }}>
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canApprove && (
                      <>
                        <button data-testid={`est-approve-${e.id}`} onClick={() => approve(e)}
                          title="Autorizar" className="btn-ghost p-1.5" style={{ color: "#86efac" }}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`est-reject-${e.id}`} onClick={() => reject(e)}
                          title="Rechazar" className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {["autorizada", "pagada"].includes(e.status) && ["admin", "contador", "auxiliar"].includes(user?.role) && (
                      <button data-testid={`est-invoice-${e.id}`} onClick={() => invoiceEstimacion(e)}
                        title="Facturar estimación (formato IGIFE)" className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest"
                        style={{ color: "#86efac", border: "1px solid #16a34a" }}>
                        <Receipt className="w-3 h-3 inline mr-1" />Facturar
                      </button>
                    )}
                    {canPay && (
                      <button data-testid={`est-pay-${e.id}`} onClick={() => pay(e)}
                        title="Marcar pagada" className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">
                        <DollarSign className="w-3 h-3 inline mr-1" />Pagar
                      </button>
                    )}
                    {e.status !== "pagada" && (user?.role === "admin" || user?.role === "contador") && (
                      <button onClick={() => remove(e)} title="Eliminar" className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="border-t px-4 py-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs"
                    style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.4)" }}>
                    <Kv label="Subtotal" value={fmtMXN(e.subtotal_period)} />
                    <Kv label={`IVA (${e.iva_pct}%)`} value={fmtMXN(e.iva_amount)} />
                    <Kv label="Total" value={fmtMXN(e.total_period)} />
                    <Kv label="% facturado" value={`${(e.financial_cumulative_pct || 0).toFixed(1)}%`} />
                    {(e.amortizations || []).length > 0 && (
                      <div className="md:col-span-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Amortizaciones</div>
                        {e.amortizations.map((a, i) => (
                          <div key={i} className="text-slate-300">
                            <span className="text-slate-500">−</span> {a.label}: <b className="font-mono">{fmtMXN(a.amount)}</b>
                          </div>
                        ))}
                      </div>
                    )}
                    {(e.retentions || []).length > 0 && (
                      <div className="md:col-span-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Retenciones</div>
                        {e.retentions.map((r, i) => (
                          <div key={i} className="text-slate-300">
                            <span className="text-slate-500">−</span> {r.label} ({r.type === "pct" ? `${r.value}%` : "fijo"}): <b className="font-mono">{fmtMXN(r.amount)}</b>
                          </div>
                        ))}
                      </div>
                    )}
                    {(e.items || []).length > 0 && (
                      <div className="md:col-span-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Partidas ({e.items.length})</div>
                        <div className="space-y-0.5 max-h-32 overflow-y-auto">
                          {e.items.map((it, i) => (
                            <div key={i} className="text-slate-300 flex items-center gap-2">
                              <span className="text-slate-500 w-5">{i + 1}.</span>
                              <span className="flex-1 truncate">{it.description}</span>
                              <span className="text-slate-500">{it.qty_period} {it.unit || ""}</span>
                              <span className="font-mono w-24 text-right">{fmtMXN(it.amount_period)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {e.rejected_reason && (
                      <div className="md:col-span-4 p-2 rounded-sm" style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)" }}>
                        <div className="flex items-start gap-1.5 text-[11px]">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" style={{ color: "#fca5a5" }} />
                          <div><b style={{ color: "#fca5a5" }}>RECHAZADA:</b> <span className="text-slate-300">{e.rejected_reason}</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <EstimacionModal
          contract={contract}
          budgetItems={budgetItems}
          existing={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); load(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono text-sm text-slate-200 tabular-nums">{value}</div>
    </div>
  );
}

// ===========================================================================
// Modal para crear / editar estimación
// ===========================================================================
function EstimacionModal({ contract, budgetItems, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() => existing
    ? { ...EMPTY, ...existing }
    : { ...EMPTY });
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    const sub = (form.items || []).reduce((s, it) => s + ((parseFloat(it.qty_period) || 0) * (parseFloat(it.unit_price) || 0)), 0);
    const iva = sub * (parseFloat(form.iva_pct) || 0) / 100;
    const amort = (form.amortizations || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const ret = (form.retentions || []).reduce((s, r) => {
      if (r.type === "pct") return s + sub * (parseFloat(r.value) || 0) / 100;
      return s + (parseFloat(r.value) || 0);
    }, 0);
    return {
      subtotal: Math.round(sub * 100) / 100,
      iva: Math.round(iva * 100) / 100,
      total: Math.round((sub + iva) * 100) / 100,
      amort_total: Math.round(amort * 100) / 100,
      ret_total: Math.round(ret * 100) / 100,
      net: Math.round((sub + iva - amort - ret) * 100) / 100,
    };
  }, [form.items, form.iva_pct, form.amortizations, form.retentions]);

  // ----- Items -----
  const addItem = (budgetItem = null) => {
    const newItem = budgetItem
      ? {
          ...EMPTY_ITEM,
          budget_item_id: budgetItem.id,
          description: budgetItem.description || budgetItem.concept || "Partida",
          unit: budgetItem.unit || "",
          unit_price: budgetItem.unit_price || budgetItem.price || 0,
        }
      : { ...EMPTY_ITEM };
    setForm({ ...form, items: [...(form.items || []), newItem] });
  };
  const updateItem = (i, key, val) => {
    const next = [...form.items];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, items: next });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  // ----- Amortizaciones (sugerir automáticamente) -----
  const suggestAmortizations = async () => {
    try {
      const { data } = await api.get(
        `/contracts/${contract.id}/estimaciones/utils/suggest-amortizations`,
        { params: { subtotal_period: totals.subtotal } },
      );
      setForm({ ...form, amortizations: data || [] });
      toast.success(`${data.length} amortización(es) sugeridas según los anticipos del contrato`);
    } catch { toast.error("No se pudieron sugerir"); }
  };
  const addAmort = () => setForm({ ...form, amortizations: [...(form.amortizations || []), { label: "", advance_pct: 0, amount: 0, automatic: false }] });
  const updateAmort = (i, key, val) => {
    const next = [...form.amortizations];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, amortizations: next });
  };
  const removeAmort = (i) => setForm({ ...form, amortizations: form.amortizations.filter((_, idx) => idx !== i) });

  // ----- Retenciones -----
  const addRet = () => setForm({ ...form, retentions: [...(form.retentions || []), { label: "", type: "pct", value: 0 }] });
  const updateRet = (i, key, val) => {
    const next = [...form.retentions];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, retentions: next });
  };
  const removeRet = (i) => setForm({ ...form, retentions: form.retentions.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.items || []).length) {
      toast.error("Agrega al menos una partida");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        contract_id: contract.id,
        folio: form.folio || null,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        iva_pct: parseFloat(form.iva_pct) || 16,
        items: form.items,
        amortizations: form.amortizations,
        retentions: form.retentions,
        physical_progress_pct: parseFloat(form.physical_progress_pct) || 0,
        notes: form.notes || null,
      };
      if (existing) {
        await api.patch(`/estimaciones/${existing.id}`, payload);
        toast.success("Estimación actualizada");
      } else {
        await api.post(`/contracts/${contract.id}/estimaciones`, payload);
        toast.success("Estimación creada");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl panel-strong max-h-[92vh] overflow-y-auto"
        data-testid="estimacion-modal">
        <div className="px-6 py-3 border-b flex items-center justify-between sticky top-0 z-10"
          style={{ borderColor: "var(--border)", background: "rgba(5,8,18,0.95)" }}>
          <div>
            <div className="label-mono">// {existing ? "EDITAR" : "NUEVA"} ESTIMACIÓN</div>
            <h2 className="font-display font-bold text-lg text-slate-100">
              {existing ? `Estimación #${String(existing.number).padStart(2, "0")}` : "Nueva estimación"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Período */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <F label="Folio">
              <input value={form.folio || ""} onChange={(e) => setForm({ ...form, folio: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </F>
            <F label="Inicio del período">
              <input type="date" value={form.period_start || ""} onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </F>
            <F label="Fin del período">
              <input type="date" value={form.period_end || ""} onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="input-cyber px-3 py-2 text-sm w-full" />
            </F>
            <F label="% IVA">
              <input type="number" step="0.01" min="0" max="100"
                data-testid="est-iva-pct"
                value={form.iva_pct}
                onChange={(e) => setForm({ ...form, iva_pct: parseFloat(e.target.value) || 0 })}
                className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" />
            </F>
            <F label="% Avance físico al cierre">
              <input type="number" step="0.1" min="0" max="100" value={form.physical_progress_pct || 0}
                onChange={(e) => setForm({ ...form, physical_progress_pct: parseFloat(e.target.value) || 0 })}
                className="input-cyber px-3 py-2 text-sm w-full text-right" />
            </F>
          </div>

          {/* Items */}
          <Section title="PARTIDAS EJECUTADAS" actions={
            <div className="flex items-center gap-1.5">
              {(budgetItems || []).length > 0 && (
                <select
                  data-testid="add-from-budget"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const bi = budgetItems.find((b) => b.id === e.target.value);
                    if (bi) addItem(bi);
                    e.target.value = "";
                  }}
                  defaultValue=""
                  className="input-cyber px-2 py-1 text-xs"
                >
                  <option value="" style={{ background: "#0a0f1d" }}>+ Desde presupuesto…</option>
                  {budgetItems.filter((b) => b.level === "item").map((b) => (
                    <option key={b.id} value={b.id} style={{ background: "#0a0f1d" }}>
                      {(b.description || b.concept || "").slice(0, 50)}
                    </option>
                  ))}
                </select>
              )}
              <button type="button" onClick={() => addItem()} className="btn-ghost px-2 py-1 text-xs uppercase tracking-widest flex items-center gap-1">
                <Plus className="w-3 h-3" /> Libre
              </button>
            </div>
          }>
            {(form.items || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic py-4 text-center">Sin partidas. Agrega desde el presupuesto o captura libre.</div>
            ) : (
              <div className="space-y-1.5">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center text-xs">
                    <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)}
                      placeholder="Descripción" className="input-cyber px-2 py-1.5 text-xs col-span-4" />
                    <input value={it.unit || ""} onChange={(e) => updateItem(i, "unit", e.target.value)}
                      placeholder="m³" className="input-cyber px-2 py-1.5 text-xs col-span-1 text-center" />
                    <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                      placeholder="P.U." className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" />
                    <input type="number" step="0.01" value={it.qty_period} onChange={(e) => updateItem(i, "qty_period", parseFloat(e.target.value) || 0)}
                      placeholder="Cant. período" className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" />
                    <input type="number" step="0.01" value={it.qty_cumulative} onChange={(e) => updateItem(i, "qty_cumulative", parseFloat(e.target.value) || 0)}
                      placeholder="Cant. acum." className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" />
                    <button type="button" onClick={() => removeItem(i)}
                      className="btn-ghost p-1 col-span-1" style={{ color: "#fca5a5" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Resumen rápido */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <SummaryCell label="Subtotal" value={fmtMXN(totals.subtotal)} />
            <SummaryCell label={`IVA (${form.iva_pct}%)`} value={fmtMXN(totals.iva)} />
            <SummaryCell label="Total" value={fmtMXN(totals.total)} />
            <SummaryCell label="Neto a pagar" value={fmtMXN(totals.net)} accent />
          </div>

          {/* Amortizaciones */}
          <Section title="AMORTIZACIONES DE ANTICIPO" actions={
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={suggestAmortizations} disabled={totals.subtotal <= 0}
                className="btn-ghost px-2 py-1 text-xs uppercase tracking-widest disabled:opacity-50"
                style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
                ★ Sugerir automáticas
              </button>
              <button type="button" onClick={addAmort} className="btn-ghost px-2 py-1 text-xs uppercase tracking-widest">
                <Plus className="w-3 h-3 inline" /> Manual
              </button>
            </div>
          }>
            {(form.amortizations || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic py-2 text-center">Sin amortizaciones. Si tu contrato tiene anticipos, usa &quot;Sugerir automáticas&quot;.</div>
            ) : (
              <div className="space-y-1.5">
                {form.amortizations.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center text-xs">
                    <input value={a.label || ""} onChange={(e) => updateAmort(i, "label", e.target.value)}
                      placeholder="Concepto" className="input-cyber px-2 py-1.5 text-xs col-span-7" />
                    <input type="number" step="0.01" value={a.advance_pct || 0} onChange={(e) => updateAmort(i, "advance_pct", parseFloat(e.target.value) || 0)}
                      placeholder="% Ant." className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" title="% del anticipo" />
                    <input type="number" step="0.01" value={a.amount || 0} onChange={(e) => updateAmort(i, "amount", parseFloat(e.target.value) || 0)}
                      placeholder="Monto" className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" />
                    <button type="button" onClick={() => removeAmort(i)} className="btn-ghost p-1 col-span-1" style={{ color: "#fca5a5" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Retenciones */}
          <Section title="RETENCIONES (manuales)" actions={
            <button type="button" onClick={addRet} className="btn-ghost px-2 py-1 text-xs uppercase tracking-widest">
              <Plus className="w-3 h-3 inline" /> Agregar
            </button>
          }>
            {(form.retentions || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic py-2 text-center">Sin retenciones. Agrega 5 al millar CMIC, 2 al millar inspección, etc.</div>
            ) : (
              <div className="space-y-1.5">
                {form.retentions.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center text-xs">
                    <input value={r.label || ""} onChange={(e) => updateRet(i, "label", e.target.value)}
                      placeholder="Ej. 5 al millar CMIC" className="input-cyber px-2 py-1.5 text-xs col-span-7" />
                    <select value={r.type} onChange={(e) => updateRet(i, "type", e.target.value)}
                      className="input-cyber px-2 py-1.5 text-xs col-span-2">
                      <option value="pct" style={{ background: "#0a0f1d" }}>%</option>
                      <option value="fixed" style={{ background: "#0a0f1d" }}>$ fijo</option>
                    </select>
                    <input type="number" step="0.01" value={r.value || 0} onChange={(e) => updateRet(i, "value", parseFloat(e.target.value) || 0)}
                      placeholder={r.type === "pct" ? "0.5" : "1000"}
                      className="input-cyber px-2 py-1.5 text-xs col-span-2 text-right font-mono" />
                    <button type="button" onClick={() => removeRet(i)} className="btn-ghost p-1 col-span-1" style={{ color: "#fca5a5" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <F label="Notas">
            <textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-cyber px-3 py-2 text-sm w-full" />
          </F>
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-2 sticky bottom-0"
          style={{ borderColor: "var(--border)", background: "rgba(5,8,18,0.95)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" data-testid="est-submit-btn" disabled={saving}
            className="btn-primary px-6 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {existing ? "Guardar cambios" : "Crear estimación"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, actions, children }) {
  return (
    <div className="panel">
      <div className="px-3 py-2 border-b flex items-center justify-between"
        style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.6)" }}>
        <div className="label-mono">// {title}</div>
        {actions}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}
function F({ label, children }) {
  return <div><label className="label-mono block mb-1">{label}</label>{children}</div>;
}
function SummaryCell({ label, value, accent }) {
  return (
    <div className="panel p-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-base font-bold tabular-nums ${accent ? "text-cyan-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}
